import { memo, useEffect, useState } from "react";
import { Handle, Position } from "reactflow";
import { Avatar, Button, List, Popover, Typography } from "antd";
import { Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import Option from "../../assets/icons/Menu.svg";
import DeleteConfirmModel from "../dashboard/DashboardMain/DeleteOpenModel/DeleteConfirmModel";
import {
  findPreviousNode,
  processString,
} from "../../utilities/Nodes/getConnectedChildNodes";
import { useTranslation } from "react-i18next";
import { StepsNodeProps } from "../../utilities/types/common";
import { validateUserInput } from "../../utilities/Nodes/commonFunctions";
import { calculatePopoverStyles } from "../../utilities/utils";
import { useFlowDataContext } from "../../context/workbench/FlowDataContext";
import { useNodeDataContext } from "../../context/workbench/DataNodeContext";
import { useViewPortContext } from "../../context/workbench/ViewPortContext";
import { useZoomContext } from "../../context/workbench/ZoomContext";
import { usePermission } from "../../hooks/usePermission";

const StepsNode = ({ data, id }: StepsNodeProps) => {
  const { t } = useTranslation("common");
  const { profileName, profileImage } = useSelector((state: any) => state.static);
  const flowDataContext = useFlowDataContext();
  const dataNodeContext = useNodeDataContext();
  const viewPortContext = useViewPortContext();
  const zoomContext = useZoomContext();
  const { canView, canEdit } = usePermission();

  const [open, setOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [newContent, setNewContent] = useState<string>("");
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const CHARACTER_LIMIT = 80;

  useEffect(() => {
    if (data?.content) {
      const contentArray = Array.isArray(data?.content)
        ? data?.content
        : data?.content
          .split("\n")
          .map((line: any) => line.trim())
          .filter(Boolean);
      setSteps(contentArray); // Ensure steps is an array of trimmed lines
      setNewContent(contentArray.join("\n")); // Initialize the textarea with joined content
      // setIsInitialized(true);
    } else {
      setNewContent("");
      setSteps([]);
    }
  }, [data?.content]);

  useEffect(() => {
    setOpen(false);
  }, [viewPortContext?.viewportXvalue, viewPortContext?.viewportYvalue])

  const handleOnchange = (e: any) => {
    let input = e?.target?.value
    setNewContent(validateUserInput(input));
  }

  const handleStartEditing = () => {
    setNewContent(Array.isArray(steps) ? steps.join("\n") : "");
    setIsEditing(true);
  };

  const handleBlur = () => {
    const contentArray = newContent
      .split("\n")
      .filter((line) => line.trim() !== "");
    const contentString: any = contentArray.join("\n");

    setSteps(contentArray);
    setIsEditing(false);

    if (typeof data.onStepsUpdate === "function") {
      data.onStepsUpdate(id, contentString);
    }
  };

  const isSelected = dataNodeContext?.selectedNodeId === id;

  const renderSteps = () => {
    if (!Array.isArray(steps) || steps.length === 0) {
      return (
        <div
          className="text-center p-3 empty-text"
          onClick={() => { canEdit && canView && setIsEditing(true) }}
        >
          {t('workbench.stepsNode.stepsContent')}
        </div>
      );
    }

    const numberedSteps = steps.map((step, index) => `${index + 1}. ${step}`);
    const combinedContent = numberedSteps.join("\n");

    // Check both conditions for truncation
    const shouldTruncateByLength = numberedSteps.length > 2;
    const shouldTruncateByChars = combinedContent.length > CHARACTER_LIMIT;
    const shouldTruncate = shouldTruncateByLength || shouldTruncateByChars;

    let displayContent = combinedContent;

    if (shouldTruncate) {
      if (shouldTruncateByLength) {
        // Show only first 3 steps if truncating by length
        displayContent = numberedSteps.slice(0, 3).join("\n");
      } else {
        // Show truncated content by character limit
        displayContent = `${combinedContent.slice(0, CHARACTER_LIMIT)}`;
      }
    }

    return (
      <div className="steps-content" onClick={handleStartEditing}>
        <div className="content-wrapper">
          <Typography.Paragraph ellipsis={{ rows: 2 }} className="mb-1">{displayContent}</Typography.Paragraph>
          {shouldTruncate && !isSelected && (
            <span
              className="read-more-less"
              onClick={(e: any) => {
                e.stopPropagation();
                selectNode();
              }}
            >
              Read More
            </span>
          )}
        </div>
      </div>
    );
  };

  const selectNode = () => {
    if (id && data) {
      dataNodeContext.getNodeData(id, data);
    }
  };

  const previousNode = findPreviousNode(id, flowDataContext?.edges, flowDataContext?.nodes);

  const handleFocus = (e: any) => {
    const length = e.target.value.length;
    e.target.setSelectionRange(length, length);
    e.target.scrollTop = e.target.scrollHeight;
  };

  // Calculate popover styles based on zoom level
  const popoverStyle = calculatePopoverStyles(zoomContext?.workbenchZoomLevel);

  return (
    <div
      className={isSelected ? "node steps-node selected" : "node steps-node"}
    >
      <div className="node-header d-flex justify-content-between align-items-center">
        <div className="card border-0 w-100 node-inner-card bg-transparent p-0">
          <div className="card-header bg-transparent border-0 p-0">
            <div className="d-flex justify-content-between">
              <div className="d-flex align-items-center mt-2 gap-2">
                {profileImage ? (
                  <Avatar src={profileImage} size={"default"} />
                ) : (
                  <Avatar>{processString(profileName)}</Avatar>
                )}
              </div>
              <div>
                {canView && canEdit && (
                  <Popover
                    content={
                      <div className="card-action-options">
                        <List className="w-100">
                          <List.Item>
                            <Button
                              className="action-btn"
                              onClick={() => {
                                setOpen(false);
                                handleStartEditing();
                              }}
                            >
                              {t('buttons.edit')}
                            </Button>
                          </List.Item>
                          <List.Item>
                            <Button
                              className="action-btn"
                              onClick={() => {
                                setOpen(false);
                                setIsDeleteModelOpen(true);
                              }}
                            >
                              {t('buttons.delete')}
                            </Button>
                          </List.Item>
                          {previousNode !== null && (
                            <List.Item>
                              <Button
                                className="action-btn"
                                onClick={() => {
                                  setOpen(false);
                                  data.onDeLink?.(id);
                                }}
                              >
                                {t('buttons.delink')}
                              </Button>
                            </List.Item>
                          )}
                        </List>
                      </div>
                    }
                    trigger="hover"
                    arrow={false}
                    open={open}
                    placement="right"
                    overlayClassName="add-node-popover"
                    onOpenChange={setOpen}
                    overlayInnerStyle={popoverStyle.overlayInnerStyle}
                    overlayStyle={popoverStyle.overlayStyle}
                  >
                    <Button className="action-btn">
                      <Image src={Option} alt="Option" />
                    </Button>
                  </Popover>
                )}
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            <p className="inner-title" onClick={() => setIsEditing(true)}>
              {t('answer')} <br />
            </p>
            {isEditing ? (
              <textarea
                className="node-textarea"
                rows={3}
                value={newContent}
                onChange={(e) => handleOnchange(e)}
                onBlur={handleBlur}
                autoFocus
                placeholder={t('answerPlaceholder')}
                onFocus={handleFocus}
              />
            ) : (
              <>{renderSteps()}</>
            )}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className="handle" />
      <Handle type="target" position={Position.Left} className="handle" />
      <DeleteConfirmModel
        visible={isDeleteModelOpen}
        onConfirm={() => {
          setOpen(false);
          data.onRemoveSingleConnected?.(id);
        }}
        onCancel={() => setIsDeleteModelOpen(false)}
        title={t('deleteModal.title')}
        content={t('deleteModal.description')}
        okText={t('buttons.confirm')}
        cancelText={t('buttons.cancel')}
      />
    </div>
  );
};

export default memo(StepsNode);
